import type { FunctionComponent } from 'react';
import { defineSlotComponent, findAllForSlot, findFirstForSlot } from '../../layouts/slots';
import { ButtonGroup, TooltipButton } from '../../generic/Button';
import { SearchInput } from '../../generic/Forms';
import styles from './Settings.module.less';

type SettingsPageTableActionsComponent = FunctionComponent & {
  TooltipButton: typeof TooltipButton;
  SearchInput: typeof SearchInput;
};

/**
 * Some settings pages have a tables of items (ie: Users, Teams, Roles, etc.),
 * and often there are "actions" such as searching the table, or buttons for creating new items.
 * This component is meant to act as a wrapper for this pattern.
 * It will position a Button to the left, and give the rest of the horizontal space for the search input.
 *
 * You can place multiple buttons, but only one SearchInput.
 *
 * @example
 * <SettingsPageTableActions>
 *   <SettingsPageTableActions.Button onClick={() => {}}>Create Team</SettingsPageTableActions.Button>
 *   <SettingsPageTableActions.SearchInput value={searchValue} onChange={setSearchValue} />
 * </SettingsPageTableActions>
 */
const SettingsPageTableActions: SettingsPageTableActionsComponent = ({ children }) => {
  const buttons = findAllForSlot('TooltipButton', children);
  const searchInput = findFirstForSlot('SearchInput', children);

  return (
    <div className={styles.settingsPageTableActions}>
      {buttons?.length > 1 ? (
        <ButtonGroup>
          {buttons}
        </ButtonGroup>
      ) : (
        <span className="inline-block">
          {buttons}
        </span>
      )}
      {searchInput}
    </div>
  );
};

SettingsPageTableActions.TooltipButton = defineSlotComponent('TooltipButton', TooltipButton);
//  Note: we are casting here to get around a bug where forwardRef doesn't infer the correct Component type
SettingsPageTableActions.SearchInput = defineSlotComponent('SearchInput', SearchInput) as typeof SearchInput;

export default SettingsPageTableActions;
